<template>
<div class="chapter">
<div class="inner_page">
<div class="section">
<div class="header">
<div class="hgroup">
<h3></h3>
<h1>Chapter 1</h1>
</div>
</div>
<p class="first">
<span style="font-size: 1em; line-height: 1.5; text-indent: 1.5em;">The November wind picked up a wad of paper, whipped it past the stone buildings and out the other side of the library steps, leaving the central quadrangle litter-free. The grass, freeze-dried a month ago by Halloween frost, hid under a crust of brown leaves. The old maple trees that lined the interior walkways of the campus were bare, branches stretched against the pale sky, stark and clean. Knots of students hustled along the sidewalk, oblivious to the austere beauty of the place, chattering, laughing, dropping books, clutching jackets tightly against the chill.</span></p>
<p class="indent">
	Quinn Mallory raced across the campus with her battered suitcase, darting past the slower students. Her auburn hair flew against her cheeks. Her red sweater was a bright splash of color, like a winter cardinal startling against the subdued landscape. She emerged at the far end of the quadrangle, bounced down a flight of marble steps, and came to a halt at the tall wrought-iron gates of the college’s formal entrance. Panting to catch her breath, she watched an elderly Volkswagen bus drive up to the gate and stop. Its brakes protested with a metallic screech, temporarily drowning out the Shirelles as they professed, full volume, “Baby it’s you.”</p>
<p class="indent">
	Stanley Markowitz pushed his mass of tangled black curls through the window. “Been waiting long?” he asked.</p>
<p class="indent">
	Quinn grinned at him, swung her suitcase onto the backseat, and climbed in. “Hours,” she answered, still gasping. “What kind of limousine service is this, anyhow? Oh, God, I’m dying.”</p>
<p class="indent">
	Stanley persuaded the bus into first gear and they shuddered past the gate onto the main road. Vanessa—Van—Huntington, who sat next to Stanley, turned around to scrutinize Quinn’s cheeks, which were steamy red from exertion. “We wouldn’t have left without you, you know,” Van said.</p>
<p class="indent">
	“What?” Quinn yelled. John Lennon and Paul McCartney were wailing “I wanna hold your hand.”</p>
<p class="indent">
	Van snapped off the radio. “I <em>said</em> we wouldn’t have left without you.”</p>
<p class="indent">
	“I was afraid I might hold you up.”</p>
<p class="indent">
	“Where’s your jacket?”</p>
<p class="indent">
	“I don’t need it,” Quinn said.</p>
<p class="indent">
	Van looked at Stanley. “There’s something wrong with her. It’s forty degrees out and she doesn’t need a coat.”</p>
<p class="indent">
	“Comes from all those years at St. Theresa’s, watching our breath in class,” Quinn said. “They never turned on the heat until Sister Superior declared winter, which usually wasn’t until December fifteenth.” She stretched luxuriously. “Let’s hear it for vacation. Come on, come on.” She raised her hands to summon applause from the front seat.</p>
<p class="indent">
	“Yay, Thanksgiving,” Van said.</p>
<p class="indent">
	Stanley glowered at them. “I’m happy for you both. I only pray this thing has a coronary so we never get to Boston.”</p>
<p class="indent">
	“Stanley’s nervous.” Van gave him a gentle smile, and he took his hand off the wheel to tug on a strand of her long dark hair.</p>
<p class="indent">
	“Not a coronary exactly,” Quinn said, listening to the engine wheeze into fourth gear. “Nervous breakdown, maybe. Your carburetor’s screwed up. Before you make another trip, you’ve <em>got</em> to let me work on the wheel alignment.”</p>
<p class="indent">
	“Yes, doctor. Anything you say, doctor,” Stanley said.</p>
<p class="indent">
	Quinn settled back into her favorite corner against the left-hand door and thought about home. She always enjoyed the trip from school to Medham. Not long, cramped hours that caused her body to ache, yet time enough for decompression. As they picked up speed she could feel herself shedding burdens—term papers, exams, after-school jobs—preoccupations that evaporated behind her like the exhaust from Stanley’s bus. Instead, her mind was cheered with images of home: the faces of her parents, the cozy comfort of the house on Gardner Street that awaited her two hours down the road. She smiled. It was lovely to be taken care of every now and then. Tomorrow morning Quinn would awaken to the smell of fresh-baked muffins and black coffee as her mother quietly entered her room with the tray. They would sit on the bed and enjoy their first-morning-home breakfast together, gossiping and catching up. Just thinking about it made her feel drowsy and warm.</p>
<p class="indent">
	Suddenly Stanley pressed hard on the accelerator and roared past a city bus that hobbled along near the curb.</p>
<p class="indent">
	“Go get ’em, tiger! Ah, there’s life in the old nag yet,” he said.</p>
<p class="indent">
	“That bus was not moving,” Quinn remarked.</p>
<p class="indent">
	“It was,” Stanley protested. “I distinctly saw the back wheel move. Vanessa, defend your chariot.”</p>
<p class="indent">
	She craned her neck to look out the back window. “Sorry, honey. That’s a dead bus.”</p>
<p class="indent">
	Stanley’s eyes in the rearview mirror were tragic—huge and brown. Quinn stuck out her tongue at them and they sparkled.</p>
<p class="indent">
	“Know what I’m going to do tonight?” Quinn said. “Set my alarm for six <span class="small">A.M.</span> so I can turn it off, roll over, and go right back to sleep.”</p>
<p class="indent">
	“Why don’t you just sleep?” Van asked.</p>
<p class="indent">
	“Didn’t you ever do that? I thought everybody did.”</p>
<p class="indent">
	“It seems like I always had to get up for <em>shul</em> on weekends,” Stanley said. “Maybe if your daddy promises to get me into Harvard Med School I’ll give him a treat and take him to Temple Emanuel on Saturday.”</p>
<p class="indent">
	“Oh, sure,” Van said.</p>
<p class="indent">
	“Right!” Stanley exclaimed heartily, pulling onto the eastbound ramp of the Massachusetts Turnpike. “Who’s for Burlington, Vermont?”</p>
<p class="indent">
	“No, no, my darling,” Van said. “Straight on to Beacon Hill.”</p>
<p class="indent">
	Stanley glanced at her. “The closer you get to Boston, the more you sound like Mummy and Daddy.”</p>
<p class="indent">
<em>“Oy vay,”</em> Van replied. She had the inflection wrong so that the expression came out sounding prim and stiff. Stanley and Quinn laughed.</p>
<p class="indent">
	“You think we’ll have turnips?” Stanley asked. “I always wanted to see a turnip in the flesh.”</p>
<p class="indent">
	“You never had turnips on Thanksgiving?” Quinn asked. “Sacrilege. What did you eat in Brooklyn?”</p>
<p class="indent">
	“Turkey with stuffing and potato pudding and noodle pudding and <em>kasha varnishkes</em> and potato pancakes. And a nice bowl of chicken soup with a couple of matzoh balls lurking on the bottom. Real sinkers.”</p>
<p class="indent">
	“The Indians shared maize with the Pilgrims, not matzoh balls,” Van said.</p>
<p class="indent">
	“In Flatbush we didn’t have Indians. Chassidim, yes. Indians, no.”</p>
<p class="indent">
	Quinn examined Van’s profile. In repose her friend’s features were classically long, rather somber. Stanley liked to call her his Modified Modigliani. But when she smiled, particularly at him, the angular lines of her face lifted and two faint dimples appeared beside her mouth, making her seem vulnerable and girlish.</p>
<p class="indent">
	“Hey, Mallory,” Stanley said. “What would your parents do if you brought home a Jewish boyfriend?”</p>
<p class="indent">
	“Enroll me in a convent.”</p>
<p class="indent">
	“Oh, you’d make a great nun,” Van laughed.</p>
<p class="indent">
	“It’s never been mentioned,” Quinn continued, “but I think they’d <em>appreciate</em> a Catholic, preferably Irish. Preferably from County Kerry, except not one of those heathen Calhouns.”</p>
<p class="indent">
	“Did they give your boyfriends a rough time?” Van asked.</p>
<p class="indent">
	“Mom never gives anybody a rough time, except for my father, and he always deserves it.” Quinn scratched her nose, remembering. “Tommy Flanagan was the only one who counted, and they thought he was the second coming of St. Patrick. Until he dumped me, of course, and then my father nearly lynched him.”</p>
<p class="indent">
	“Hey!” Van exclaimed suddenly. “Aren’t you going to see him this vacation?”</p>
<p class="indent">
	Quinn nodded.</p>
<p class="indent">
	“Oh <em>ho,</em> romance is in the air,” Stanley said.</p>
<p class="indent">
	“Don’t get all worked up,” Quinn muttered. “I’m going to his engagement party. Actually, I’m going to Margery O’Malley’s engagement party. Tommy Flanagan just happens to be the person she’s marrying. Otherwise I’d drop dead before I’d show up.”</p>
<p class="indent">
	Van peered at her. “Your freckles just got darker. You must really hate him.” Quinn rolled her eyes. “No, really,” Van insisted. “Look, Stan. Isn’t that amazing?”</p>
<p class="indent">
	Stanley obligingly twisted his head around to view Quinn’s freckles. “Topographical map of Ireland, right there,” he said. “How come Tommy Flannelman dumped you?”</p>
<p class="indent">
	“Flanagan. I told you. He was a shit. The only potential Marvin the Magnificent I have ever known, and he turned out to be El Creep.”</p>
<p class="indent">
	“Oh, Lord, Marvin again,” Van said. “I don’t like to disillusion you, dearest, but there’s no such thing as the perfect man.”</p>
<p class="indent">
	“I resent that,” Stanley said.</p>
<p class="indent">
	“Marvin will be perfect,” Quinn protested. “Body beautiful and mind <em>extraordinaire.</em> Four-oh average, of course. Poetic soul. Man of my dreams. Although,” she added, “if he turns up at Margery’s party, he won’t have a four-oh. I’ll be the only one there who goes to college. But he could still be brilliant. Tommy was.”</p>
<p class="indent">
	“How come he didn’t go to college, then?” Stanley asked.</p>
<p class="indent">
	“Thought it was a waste of time. He got a full scholarship to B.U., the jerk. Can you believe anything so ridiculous?”</p>
<p class="indent">
	“What’s he doing now?” Van asked.</p>
<p class="indent">
	“He works at a bank. He’s doing all right, I guess, but I haven’t seen him in three years.” She watched out the window as the Berkshire foothills smoothed out into long, flat fields. “Maybe I should have been a nun,” she murmured to herself, and fell silent. Holiday traffic was beginning to crowd the turnpike. The van clattered into the left lane to overtake a slow-moving caravan of cars. A campaign poster, remnant of the election three weeks ago, whizzed past: <em>Vote for Moore in ’64.</em></p>
<p class="indent">
	Suddenly Stanley boomed out in a hearty, man-to-man voice, “Well, stuck in the rough, were you, eh, Dr. Huntington? Guess I’d have used my seven wood or my widge.”</p>
<p class="indent">
	“Wedge!” Van howled. “Oh, Lord, it’s hopeless. The wedge is for sand traps. He’d probably use his five iron in the rough.”</p>
<p class="indent">
	“Five iron,” Stanley echoed morosely. “Can’t we talk about stickball? I bet you didn’t know one time I hit three sewers. I mean, what’s a hole in one compared to three sewers on Avenue J?”</p>
<p class="indent">
	“What?” Van and Quinn said in unison.</p>
<p class="indent">
	“You know,” Stanley explained. “Those manholes in the street. If you hit the ball past two, you were a champ, but three was Hall of Fame. Izzy Pinkowitz was a three-sewer man too. Me, and Izzy, and Jackie Halpem, of course.”</p>
<p class="indent">
	Van shook her head. “I don’t think Father would relate to sewers.”</p>
<p class="indent">
	“Aw, shucks,” Stanley said regretfully. “Better refresh my memory on putts.” His face brightened and he adopted a heavy Yiddish accent. “Oy, a Jewish voyd. I’ll explain ’im all about my <em>putz.”</em></p>
<p class="indent">
	“Oh, Stanley, for heaven’s sake, they’re not going to eat you,” Van said.</p>
<p class="indent">
	“Were they really so awful the last time?” Quinn asked.</p>
<p class="indent">
	“I was a perfect gentleman. They hated me.”</p>
<p class="indent">
	“They did not,” Van protested. “They just never saw anybody in jeans, an army shirt, and a black silk vest.”</p>
<p class="indent">
	“You’re kidding,” Quinn said.</p>
<p class="indent">
	“It was from my grandfather’s tuxedo, and I wore it out of respect for your parents,” Stanley said. “They hated me. They still hate me. Why are we doing this mad thing?” He swerved the car as if to make a U-turn.</p>
<p class="indent">
	Quinn reached into her sweater pocket and held out a metal object. “I brought you a lucky charm, so now you don’t have to be so nervous.” Stanley took his hand off the wheel and examined it. From heavy wire Quinn had fashioned a Star of David with a cross in the center. “I’m going to buy you a chain for it, but I just finished it this morning. This way, see, you’ve got all the big guns on your side.”</p>
<p class="indent">
	“I’m going to keep it with me every second,” Stanley said. “Thanks, babe.”</p>
<p class="indent">
	“May I see?” Van examined it. “It’s wonderful. Would you maybe make me one someday?”</p>
<p class="indent">
	“Sure.” Quinn thought about the pile of rejects that had accumulated on her desk as she puzzled over the talisman. It had taken two weeks to get it right.</p>
<p class="indent">
	Van stretched out a hand to touch Stanley’s cheek. “Well, there’s nothing my parents can do about the way I feel,” she said.</p>
<p class="indent">
	Stanley grabbed her fingers and began kissing them ferociously. “I can take all kinds of abuse as long as I get to sneak into your bedroom and ravage your body,” he said.</p>
<p class="indent">
	Quinn studied Van’s flushed profile and remembered the night she had watched her friends saying good night in the shadows beside the dormitory entrance. They were crushed together, their mouths locked, parting briefly only to join again. Quinn had caught a glimpse of their faces as they stared at each other in wonder. They had looked awed.</p>
<p class="indent">
	Margery’s party flashed into her head, a shimmering question mark. Marvin, she thought, where are you? It’s time. It’s time.</p>
<p class="sectiontop">
	Medham was part of the blighted area of Boston situated halfway between the central city and the suburbs. In April the trees wore a hopeful tender green veil, but they soon blackened with soot from the nearby shoe factory where John Mallory worked. In summertime the tiny front lawns were overgrown with weeds, and in September the leaves made no final dazzling display of predeath defiance; rather, they just curled into defeated husks and fell to earth beside the shabby wood-frame houses, most of which, like the Mallorys’, were divided into two sections and shared with another family. Now, in late November, the ground was a bleak crust of brown with an occasional splotch of frozen slush.</p>
<p class="indent">
	Quinn waved good-bye to Stanley and Van and marched up the steps. Before she could ring the bell, John Mallory opened the door and grasped her in a tight hug, suitcase and all.</p>
<p class="indent">
	“Daddy, I’ve missed you,” she murmured against his bony shoulder. “Hey, you’re squishing me to death.” She held him away from her and smiled into his face. For a fair-skinned man his whiskers were very dark, as were his eyebrows. Quinn had always thought the coarse auburn hair, a shade darker than her own, and the hazel eyes under dark brows made him unusually handsome.</p>
<p class="indent">
	As always, the house had a faint smell of the lemon oil her mother used to polish the piano.</p>
<p class="indent">
	“Where’s Mom?” she asked.</p>
<p class="indent">
	“In the kitchen.” John set her valise on the bottom step of the staircase.</p>
<p class="indent">
	“Naturally,” Quinn said, and started off down the hall.</p>
<p class="indent">
	“Is that you, dear?” A voice called.</p>
<p class="indent">
	“No,” Quinn answered. Ann Mallory met her at the kitchen door, and the two embraced.</p>
<p class="indent">
	“If it isn’t my girl, who is it, then?” Ann asked her.</p>
<p class="indent">
	“Um, Moll Magee.”</p>
<p class="indent">
	“God forbid,” John said. His lifetime devotion to William Butler Yeats had resulted in a family habit of referring to the poet’s wild and tragic characters as if they lived just down the block.</p>
<p class="indent">
	John went to the stove, lifted the lid off a large simmering pot, and grabbed a potato with calloused fingers.</p>
<p class="indent">
	“Sit down, John,” Ann said. “You’ll burn yourself.” Elbowing him out of the way, she began ladling out the stew. John carried two bowls to the table and started back for more, but Quinn intercepted him.</p>
<p class="indent">
	“Since when are you so gallant?” she asked, taking the dishes from his hands.</p>
<p class="indent">
	“Since your mother’s been feeling poorly.”</p>
<p class="indent">
	“Mom?” Quinn inquired.</p>
<p class="indent">
	“I got overtired working on the bazaar for St. Theresa’s. Your father’s being silly. Not that I mind him waiting on himself now and then.”</p>
<p class="indent">
	“She’s going to see Dr. Marshall next week,” John said to Quinn.</p>
<p class="indent">
	“I’m not,” Ann retorted.</p>
<p class="indent">
	“Ten thirty Tuesday morning. If I have to drag you by the toes.”</p>
<p class="indent">
	“Such a fuss over nothing, John,” she said, giving him a light flick with her dish towel. “Besides, I have a heavy schedule the beginning of the week.”</p>
<p class="indent">
	“Since your mother got promoted to administrative assistant, she thinks she runs the college,” John said to Quinn.</p>
<p class="indent">
	“She’d be good at it.”</p>
<p class="indent">
	“I’m the only person in the history department without a degree,” Ann protested. “I work hard because I don’t know enough.”</p>
<p class="indent">
	“By the time you’re finished with these research projects, you’ll know more than any of ’em,” John muttered. “And be worn out, too.”</p>
<p class="indent">
	Ann turned to roll her eyes at her daughter. Quinn had been busy inspecting her, probing for signs of illness. There were none. Ann moved efficiently about the kitchen, as always. Relieved, Quinn plunked down at the round oak table in the corner. The caned seat felt comfortably familiar beneath her. She kept her eyes on her mother.</p>
<p class="indent">
	“This chair always was a perfect fit for my ass.”</p>
<p class="indent">
	Ann turned to flash Quinn a look of reproach. Quinn stared back with eyes twinkling and recited along with her, “Oh, Quinn, honestly …”</p>
<p class="indent">
	They they both laughed and Quinn said, “Now I <em>know</em> I’m home.”</p>
<p class="indent">
	Quinn watched Ann sit down, taste the stew, wrinkle her nose critically, and reach for the pepper grinder. She had curly dark hair, creamy freckled skin, and blue eyes. Quinn’s were blue as well, but bright and clear, filled with sparkling prisms of light. Ann’s were darker, deep-sea blue, shadowed. Quinn enjoyed looking at her, though today she thought her mother did seem a little tired. Maybe it was the dying winter sunlight against her mother’s soft cheeks. Ann looked up, smiling, and Quinn decided the pallor was only in her imagination.</p>
<p class="indent">
	“You heard about Margery? She’s engaged. To Tommy Flanagan, of all people.”</p>
<p class="indent">
	Quinn held up three fingers and wagged them at her. “Once by mail, once by phone, and now in person.”</p>
<p class="indent">
	“Oh, did I really? I must be getting old. And surely Margery would tell you in <em>her</em> letters.”</p>
<p class="indent">
	“She surely would. Besides, you know I’ve got the party tonight.”</p>
<p class="indent">
	“Yes, of course.”</p>
<p class="indent">
	“You trying to tell me something, Mom?”</p>
<p class="indent">
	Before Ann could answer, John muttered, “The O’Malleys will rue the day they allowed that boy in the family.”</p>
<p class="indent">
	“I’m sure he’s perfectly nice now. Susan O’Malley’s delighted,” Ann said.</p>
<p class="indent">
	“Susan O’Malley would think Adolf Hitler was perfectly nice.”</p>
<p class="indent">
	“That was four years ago, Jake,” Quinn protested, reverting to her childhood habit of addressing her parents by their first names, or in John’s case by his nickname. It had pleased her to think of them as comrades. “We were just kids, for Christ’s sake,” she continued.</p>
<p class="indent">
	“Don’t swear, dear,” Ann said.</p>
<p class="indent">
	“You must hear a lot of cursing up at B.C., Mom. When are you going to get used to it?”</p>
<p class="indent">
	“She chews them out too, don’t think she doesn’t,” John said. “And Tom Flanagan behaved like a dog. You know, I found out from Father Riley that the Flanagans were first cousins to the O’Roarkes. Not the Dublin O’Roarkes, the farmers. My great-grandfather on the Madden side got fleeced by old man O’Roarke when he bought that pitiful little shed near the river. Took all his savings and the damn thing nearly floated away in the spring floods. Worthless, it was.”</p>
<p class="indent">
	Quinn tipped her bowl to capture the last spoonful of stew and sang with an exaggerated brogue, “The thing about John Mallory, y’ see, the man never carries a grudge. Sure, and not past the sixth or seventh generation.”</p>
<p class="indent">
	“And sure, you’re not a rogue,” Ann said with a laugh.</p>
<p class="indent">
	Quinn leaned back, balancing her chair on its hind legs. “I bet everybody’ll be at Margery’s tonight. All the old gang.”</p>
<p class="indent">
	“I think I heard Noreen’s cousin from Quincy is up,” Ann said. “You remember, dear, the tall good-looking one.”</p>
<p class="indent">
	Quinn grimaced.</p>
<p class="indent">
	“She remembers,” John said.</p>
<p class="indent">
	“Is there anyone special at school?” Ann’s voice was casual.</p>
<p class="indent">
	“No. I really don’t have time.”</p>
<p class="indent">
	“Of course you have time. Every young girl has time.”</p>
<p class="indent">
	“There’s the cafeteria job and the work at the garage. I see a lot of Gus Lenowski. We spend many a weekend together changing points and plugs.”</p>
<p class="indent">
	Ann looked closely at her daughter. “He’s … older, isn’t he?”</p>
<p class="indent">
	“Ancient. Forty if he’s a day. There isn’t anybody, Mother. I’ll probably go to my deathbed a virgin.”</p>
<p class="indent">
	Ann set her soup spoon down abruptly.</p>
<p class="indent">
	“Besides, you’ve been a career lady for two years now. How come you’re still putting the pressure on me to get married?”</p>
<p class="indent">
	“Was I?”</p>
<p class="indent">
	“You were,” John said. “There’ll be plenty of time for all that after graduation. Let her concentrate on her studies.” He turned to Quinn. “Make Phi Beta Kappa yet?”</p>
<p class="indent">
	“Speaking of pressure,” Quinn said, “don’t I always do all right?”</p>
<p class="indent">
	“All right? What’s that?”</p>
<p class="indent">
	She made a face at him.</p>
<p class="indent">
	“Leave her alone, John,” Ann said. “She just got here, for heaven’s sake.”</p>
<p class="indent">
	“That’s all right, Mom. I wouldn’t recognize him if he wasn’t surly.”</p>
<p class="indent">
	“I’m the most even-tempered person in this house,” John said. Quinn hooted.</p>
<p class="indent">
	“Did you bring home something nice to wear to the party, dear?” Ann asked Quinn.</p>
<p class="indent">
	“I have a nice greasy sweat shirt with matching cut-offs …”</p>
<p class="indent">
	“Jesus Mary!” John interrupted with mock horror. “You’ll do your mother in, girl. You know she has a weak heart.”</p>
<p class="indent">
	“Margery getting married. It’s hard to believe,” Ann remarked.</p>
<p class="indent">
	“Seems just the other night she came in here crying and carrying on, poor lass,” John said.</p>
<p class="indent">
	They all sat quietly for a moment, remembering. Several years ago when she and Quinn were still in high school, Margery had appeared at the front door late one night, asking for Ann. She refused to tell Quinn what was wrong, only insisted through her tears that she must talk to Ann, until finally Quinn got her mother out of bed. She and Margery sat in the kitchen with the door closed for almost two hours while Quinn and John tried to concentrate on Marlon Brando in <em>Viva Zapata.</em> Days later Margery confided in Quinn that she was pregnant. She miscarried soon afterward, and, as far as Quinn knew, the O’Malley family never found out.</p>
<p class="indent">
	Troubled people had always turned to Ann Mallory, and it was only during their unburdenings that the kitchen door was kept closed. Quinn would go to her room or sit by the television with the volume turned way up so that she wouldn’t be tempted to eavesdrop. As a child she had felt uneasy about those sessions. On the one hand, she was proud that people valued Ann’s advice. On the other hand, she sometimes wished it had been possible to hoard her mother’s special comforting gifts. Some things were hard to share with the general public.</p>
<p class="indent">
	“I don’t suppose you’ll tell me what it was all about?” John asked. As Ann looked at him he held up his hands. “No, never mind. Forget I asked.”</p>
<p class="indent">
	Everyone knew that with Ann a secret remained a secret. Quinn shoved back her chair and began collecting dishes.</p>
<p class="indent">
	Ann started to rise. “Yes, you’d better get a move on if you’re going to make it to the party by six.”</p>
<p class="indent">
	“Sit down, Ann,” John ordered. “I’ll do it.”</p>
<p class="indent">
	Quinn stared at him as he stacked the plates and carried them to the sink.</p>
<p class="indent">
	“Your shower,” Ann prompted.</p>
<p class="indent">
	“Yeah, thanks, okay. Stew was great, Mom.” On her way out she planted a hasty kiss on her mother’s cheek.</p>
<p class="indent">
	They listened to her clatter up the stairs.</p>
<p class="indent">
	“It’s nice to have noise,” Ann said.</p>
<p class="indent">
	John, up to his elbows in soapsuds, grinned at her from across the kitchen. “You betcha,” he said.</p>
<p class="sectiontop">
	Quinn dressed carefully. Just in case Magnificent Marvin shows up, she told herself. Why did it seem as if two weeks ago she’d been primping in front of this same mirror for the sake of Tommy Flanagan’s admiring smile? She supposed it was the absence of any important person ever since. Besides, she was sixteen when she fell for Tommy and eighteen when it was all over. Everything gets exaggerated during those years. Her hormones must have been making her crazy. She brushed her hair energetically, pleased at how the light caught its waves. It shone against her shoulders, and her blue sweater matched her eyes. Tommy was probably ugly. Maybe even fat. What the hell.</p>
<p class="indent">
	The party was in full swing when she arrived. Susan Kelly shouted, “Hey, there’s Quinn!” But immediately she was pressed up against Jim Donohue. He handed her a papercupful of beer.</p>
<p class="indent">
	“How’s our co-ed? Still learning how to be better than us poor working slobs?”</p>
<p class="indent">
	“Now tell me, Jim, how in the world could I ever be better than you?”</p>
<p class="indent">
	He gave her a squeeze. “You’re still pretty cute, Freckles. For an intellectual.”</p>
<p class="indent">
	It took her fifteen minutes to shove through the crowd to the happy couple. She managed to swallow another cup of beer on her way. When she spotted Tommy’s curly head beside the window, she gulped down a third. He watched her approach, and she caught his quick appraisal of her face and body. Margery, busy with her mother, didn’t notice.</p>
<p class="indent">
	“Hi,” Quinn said, smiling into the smoky eyes that had bewitched her way back in seventh grade when she’d had to pitch a no-hitter to make him know she was alive.</p>
<p class="indent">
	Tommy returned her smile, dipped his head for a chaste kiss. Then Margery interrupted her conversation with Mrs. O’Malley long enough to hug Quinn. Margery had an aureole of wild, frizzy brown hair that had always dismayed her. Since the boy-crazy days of junior high she had experimented with every remedy: expensive conditioners touted by Italian countesses with sleek dark tresses; creams sold exclusively in the ghetto drugstores of Roxbury—she had even talked Quinn into ironing it the night of the Junior Prom, but the result was always a disappointing thatch that was as brittle as hay, or singed kinks that exuded alarming wafts of Eau de Bonfire with every movement of her head. Quinn concluded that Tommy’s attentions must have given Margery confidence. Tonight her hair, left to its own devices, was a pillow of tangled curls. Her face was a perfect heart shape, her eyes were golden brown, and Quinn thought she had never looked prettier.</p>
<p class="indent">
	“I’m so glad you’re here,” Margery was saying. “But we won’t be able to get two words in tonight. When am I going to see you?”</p>
<p class="indent">
	“I’ll be around till Sunday,” Quinn said.</p>
<p class="indent">
	“Terrific. I’ve got so much to tell you.” Margery gave Tommy a mischievous look. “Come on, Mom, we’d better find those ashtrays or we’ll have a new pattern in the rug.”</p>
<p class="indent">
	Margery’s mother said hello, then looked from Quinn to Tommy with undisguised anxiety. But Margery escorted her toward the kitchen.</p>
<p class="indent">
	Left alone with him, Quinn said, “Congratulations. You couldn’t have done better.”</p>
<p class="indent">
	“I’m a lucky son of a bitch,” he said. The gray eyes watched her closely. “How are you, Quinn?”</p>
<p class="indent">
	“Never better. I hear you’re a banker now.”</p>
<p class="indent">
	“Yeah.”</p>
<p class="indent">
	“You like it?”</p>
<p class="indent">
	“Beats college.”</p>
<p class="indent">
	“Now, how would you know?”</p>
<p class="indent">
	“You going to start on that?” he asked.</p>
<p class="indent">
	She stared at him in silence for a moment, wondering if she had just found out why he hadn’t shown up that last Saturday night. “I bet Margery’s much too easy on you,” she said finally.</p>
<p class="indent">
	“Yup. She seems to think I’m acceptable in my present flawed state.”</p>
<p class="indent">
	“Everybody can use improvement,” Quinn said.</p>
<p class="indent">
	“Some of us are hopeless.” He reached out a long arm to grab two more cupfuls of beer from a passing tray. He handed one to Quinn. “Are you working on anybody at school?”</p>
<p class="indent">
	Quinn felt herself growing flustered and took a swallow from her cup. “No.”</p>
<p class="indent">
	“I can’t believe that. There must be all kinds of guys who need motivating. Better grades, more touchdowns—”</p>
<p class="indent">
	“Is that what you think I do?” she interrupted. “Go around like some missionary bettering people’s characters?”</p>
<p class="indent">
	She realized immediately that her tone was far too intense for his cheerful banter. She looked down into the nearly empty paper cup, and Tommy touched her shoulder lightly.</p>
<p class="indent">
	“Hey, I was just teasing.”</p>
<p class="indent">
	“And all this time I thought it was because I wouldn’t go to bed with you.” Infuriatingly, her eyes began to fill with tears.</p>
<p class="indent">
	“Quinn, what in hell did I say?”</p>
<p class="indent">
	She shook her head. “Sometimes I think that was my first mistake. I actually believed all that eyewash about going straight to hell.”</p>
<p class="indent">
	“You’re still pissed off at me,” he said. His voice was quietly incredulous.</p>
<p class="indent">
	“You broke my heart, you bastard.”</p>
<p class="indent">
	“Hey, listen, that was kid stuff. I never meant—” A lone tear trickled down her cheek and he broke off miserably, “Jesus.”</p>
<p class="indent">
	“Margery makes you happy, doesn’t she?” Quinn asked.</p>
<p class="indent">
	“Yes.”</p>
<p class="indent">
	“She’ll be good to you. She’ll make your bed and wash your socks and have your babies and never demand a thing.” He opened his mouth to protest, but she didn’t let him speak. “I’m not putting that down.”</p>
<p class="indent">
	“Like hell.”</p>
<p class="indent">
	“She’s the best-natured, kindest person on earth, and you’d just better be nice to her forever.” Quinn glanced toward the kitchen and saw Susan O’Malley staring at them. “I’m going to mingle. It’s good to see you, Tommy. All the best.” She kissed his cheek and turned to lose herself in the crush.</p>
<p class="sectiontop">
	At Thanksgiving dinner, and for the remainder of the vacation, Quinn’s parents thought she seemed quieter than usual. Instead of pursuing her customary frantic schedule of visiting and parties, the front door banging with incessant entrances and departures, she mostly stayed at home, reading magazines and tinkering with her father’s car. On Sunday afternoon Stanley and Van stopped by to pick her up.</p>
<p class="indent">
	Stanley waited until she had finished waving goodbye to Ann and John from the back window of the bus. “So, how was it?” he asked.</p>
<p class="indent">
	“Fine. What about yours?”</p>
<p class="indent">
	“Your mother is so pretty,” Van said.</p>
<p class="indent">
	“You don’t look anything like her,” Stanley commented.</p>
<p class="indent">
	“Gee, thanks,” Quinn said. “You’re both alive, so it couldn’t have been too bad.”</p>
<p class="indent">
	“Yeah, but you should see <em>them</em>,” Stanley said gleefully, “lying in a pool of blood on the pale white carpet, hatchets sticking out of their backs.”</p>
<p class="indent">
	“Stanley was wonderful,” Van said. “They couldn’t find a thing the matter with him.”</p>
<p class="indent">
	“Drove them nuts. I could feel them inspecting my nails. I wonder why they didn’t ask me for a urine specimen.”</p>
<p class="indent">
	“Oh, Stanley,” Van said.</p>
<p class="indent">
	“Who knows, they might have detected some rare disease found only among those of the Jewish persuasion. Fatal, of course, preferably within the hour.”</p>
<p class="indent">
	“They’re coming along,” Van said firmly, and turned around to ask Quinn, “Was Marvin at the party?”</p>
<p class="indent">
	“Not that I noticed. It was very crowded. Did you do anything special?”</p>
<p class="indent">
	“Wait a minute,” Stanley said. “What about Flannelmouth? Has he reformed?”</p>
<p class="indent">
	“Hard to tell. Did you pass the golf test?”</p>
<p class="indent">
	“God damn it, what happened at the party?” Stanley bellowed.</p>
<p class="indent">
	“Nothing. I said ‘hello’ and he said ‘hello’ and we shot the breeze for a while and that was that. I think he’ll be extremely happy with Margery.” Her voice was even. Van peered into her face, and Quinn smiled back sweetly. After that she fell silent, watching out the window.</p>
<p class="indent">
	Finally Stanley complained about the unnaturally low noise level in the backseat, and Van said, “Tell her about the Great Freudian Slip.”</p>
<p class="indent">
	“You tell her.”</p>
<p class="indent">
	“Oh, no, I’m much too humiliated.”</p>
<p class="indent">
	“We’re all sitting at the table looking like a Norman Rockwell painting,” Stanley related, “and Mrs. Huntington gives me this tight little smile, hands me a thimbleful of coffee, and says, ‘Stanley, wouldn’t you like another piece of this lovely chocolate kike?’ ”</p>
<p class="indent">
	“She didn’t! I don’t believe you,” Quinn howled.</p>
<p class="indent">
	Van shook her head. “Do you think maybe I was adopted?”</p>
<p class="indent">
	“That got a rise out of her,” Stanley said, referring to Quinn. But she had already slipped back into her reverie. “Mallory, what in <em>hell</em> is going on with you?”</p>
<p class="indent">
	“Nothing. I’m thinking.” It had begun to snow, and Quinn watched the fine dry powder whirl into fanciful shapes beside the road as the bus roared past.</p>
<p class="indent">
	“Yes, but what about?” Van asked.</p>
<p class="indent">
	“Just working on Marvin, that’s all.”</p>
<p class="indent">
	“That’s all,” Van echoed.</p>
<p class="indent">
	“If he wasn’t at the party, where’d you meet him?” Stanley asked.</p>
<p class="indent">
	“I didn’t. But I’m going to. Soon. Now leave me alone. I’m plotting.”</p>
<p class="indent">
	Van shrugged at Stanley. For the rest of the trip all conversation was confined to the front seat.</p>
</div>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// 007_Chapter_01.vue?7724a3bd